import './choose-location.scss'

import { ToggleGroup, VisuallyHidden } from 'radix-ui'
import { useCallback, useState } from 'react'

import { GameDataServiceAPI } from '@API/data-services/game-data-service'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import PreviousChevron from '@Assets/icons/previous-chevron.svg'
import { Button } from '@Components/button/button'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { ClaimCenterCheckboxCards } from '@Components/claim-flow/screens/choose-location/claim-center-card'
import { useQuery } from '@tanstack/react-query'
import { LoadingIndicator } from '@Components/loading-indicator/loading-indicator'

const CLASS_NAME = 'choose-location-screen'

export type ChooseLocationProps = {
  initialValue: string[]
  onNext: (locations: string[]) => void
  onBack: () => void
}

export const ChooseLocationScreen = ({ initialValue, onBack, onNext }: ChooseLocationProps) => {
  const [locations, setLocations] = useState<string[]>(initialValue)

  const { data: claimCenters, isLoading } = useQuery({
    queryKey: [`claim-centers`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      const result = await GameDataServiceAPI.getClaimCenters()
      return result.sort((a, b) => a.name.localeCompare(b.name))
    },
  })

  const locationSelectHandler = useCallback((locs: string[]) => {
    setLocations(locs.sort((a, b) => b.localeCompare(a)))
  }, [])

  const renderLocations = () => {
    return claimCenters?.map((loc) => {
      return (
        <ClaimCenterCheckboxCards
          key={`${loc.id}-check-card`}
          details={loc}
          isChecked={locations.includes(loc.id)}
        />
      )
    })
  }

  return (
    <section className={CLASS_NAME}>
      <LoadingIndicator active={isLoading} />

      <fieldset aria-describedby="location-required-description">
        <legend id="location-instructions">
          Pick one or more locations to view availability <span aria-label="required">*</span>
          <VisuallyHidden.Root id="location-required-description">
            At least one location must be selected to continue.
          </VisuallyHidden.Root>
        </legend>

        <ToggleGroup.Root
          className={`${CLASS_NAME}__location-group`}
          type="multiple"
          value={locations}
          onValueChange={locationSelectHandler}
          aria-required="true">
          {renderLocations()}
        </ToggleGroup.Root>
      </fieldset>

      <FormFloatingFooter>
        <section className={`floating-footer__buttons`}>
          <Button
            className={`floating-footer__next-button`}
            disabled={locations.length === 0}
            onClick={() => onNext(locations)}>
            Next{' '}
          </Button>
          <Button
            className={`floating-footer__previous-button`}
            onClick={onBack}
            aria-label="Go back to previous step">
            <img src={PreviousChevron} alt="" aria-hidden="true" />
          </Button>
        </section>
      </FormFloatingFooter>
    </section>
  )
}
